import { ReportingSettlementReport } from '~/typings/API';
import { getPrettyFilename } from '~/util/downloadFile';
import { titleCase } from '~/util/formatString';

export const getFilenameForStatementReport = (entry: ReportingSettlementReport, ext: string) => {
  let fileType;
  switch (entry.type) {
    case 'bank_account_daily_statement':
      fileType = 'Daily Statement';
      break;
    case 'bank_account_monthly_statement':
      fileType = 'Monthly Statement';
      break;
    case 'bank_account_transaction':
      fileType = 'Transaction Report';
      break;
    case 'bank_account_summary':
      fileType = 'Summary Report';
      break;
    default:
      fileType = 'Statement';
  }

  const fileDescription = [];
  if (entry.statementSubjectDescription) {
    fileDescription.push(entry.statementSubjectDescription);
  }
  if (entry.fromDate && entry.toDate) {
    fileDescription.push(`${entry.fromDate} to ${entry.toDate}`);
  }

  return getPrettyFilename({
    fileType,
    fileDescription,
    ext,
  });
};

export const getStatementPeriod = (type?: string, toDate?: string) => {
  const [year, month, day] = toDate?.split('-') ?? [];
  if (!year || !month || !day) return undefined;
  const date = new Date(Date.UTC(Number(year), Number(month) - 1, Number(day)));
  const monthName = date.toLocaleDateString('en-US', { month: 'long', timeZone: 'UTC' });
  if (type === 'bank_account_monthly_statement') {
    return { label: `${monthName} ${year}`, calendarNumber: Number(month) };
  }
  if (type === 'bank_account_daily_statement') {
    return { label: `${monthName} ${Number(day)}, ${year}`, calendarNumber: Number(day) };
  }
  return undefined;
};

const getTimeZoneOffsetMs = (instant: Date, timeZone: string) => {
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat('en-US', {
      timeZone,
      hourCycle: 'h23',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    })
      .formatToParts(instant)
      .map((part) => [part.type, part.value])
  );
  const wallTime = Date.UTC(
    Number(parts.year),
    Number(parts.month) - 1,
    Number(parts.day),
    Number(parts.hour),
    Number(parts.minute),
    Number(parts.second)
  );
  return wallTime - Math.floor(instant.getTime() / 1000) * 1000;
};

const STATEMENT_TIME_ZONE = 'America/Los_Angeles';
const STATEMENT_TYPES = new Set(['bank_account_daily_statement', 'bank_account_monthly_statement']);

const getZonedTime = (date: string, timeZone: string, hour = 0, minute = 0, second = 0) => {
  const [year, month, day] = date.split('-').map(Number);
  const wallTime = Date.UTC(year, month - 1, day, hour, minute, second);
  const guess = wallTime - getTimeZoneOffsetMs(new Date(wallTime), timeZone);
  return new Date(wallTime - getTimeZoneOffsetMs(new Date(guess), timeZone));
};

export const shiftDate = (date: string | undefined, days: number) => {
  if (!date) return undefined;
  const [year, month, day] = date.split('-').map(Number);
  return new Date(Date.UTC(year, month - 1, day + days)).toISOString().slice(0, 10);
};

export const getStatementPeriodRange = (type?: string, fromDate?: string, toDate?: string, timeZone?: string) => {
  if (!fromDate || !toDate) return undefined;
  try {
    if (type && STATEMENT_TYPES.has(type)) {
      return {
        start: getZonedTime(fromDate, STATEMENT_TIME_ZONE, 16, 1),
        end: getZonedTime(toDate, STATEMENT_TIME_ZONE, 16, 0, 59),
      };
    }
    if (!timeZone) return undefined;
    return {
      start: getZonedTime(fromDate, timeZone),
      end: new Date(getZonedTime(shiftDate(toDate, 1)!, timeZone).getTime() - 1),
    };
  } catch {
    return undefined;
  }
};

const REPORT_TYPE_LABELS: Record<string, string> = {
  bank_account_daily_statement: 'Daily Statement',
  bank_account_monthly_statement: 'Monthly Statement',
  bank_account_summary: 'Bank Account Summary Report',
  bank_account_transaction: 'Bank Account Transaction Report',
};

export const getReportTypeLabel = (type?: string) =>
  REPORT_TYPE_LABELS[type ?? ''] ?? (type ? titleCase(type.replaceAll('_', ' ')) : 'Report');

export const formatReportDate = (date: string) => {
  const [year, month, day] = date.split('-').map(Number);
  return new Date(Date.UTC(year, month - 1, day)).toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });
};

export const getReportPeriodLabel = (fromDate?: string, toDate?: string) => {
  if (!fromDate || !toDate) return undefined;
  if (fromDate === toDate) return formatReportDate(toDate);
  return `${formatReportDate(fromDate)} → ${formatReportDate(toDate)}`;
};
